import {useState, useCallback, type ReactNode, type ComponentProps} from 'react'
import {Check, Copy} from 'lucide-react'
import {cn} from '@/shared/lib/cn'
import {Button} from '.'

interface CopyButtonProps extends Omit<ComponentProps<typeof Button>, 'children' | 'onClick'> {
  /** Text to copy to clipboard */
  textToCopy: string
  /** Button text when idle */
  children?: ReactNode
  /** Text shown after successful copy */
  copiedText?: string
  /** Duration in ms to show "Copied!" state */
  copiedDuration?: number
  /** Callback after successful copy */
  onCopy?: () => void
}

export const CopyButton = ({
  textToCopy,
  children = 'Копировать',
  copiedText = 'Скопировано!',
  copiedDuration = 2000,
  onCopy,
  variant = 'secondary',
  className,
  ...props
}: CopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(textToCopy)
      setIsCopied(true)
      onCopy?.()

      setTimeout(() => {
        setIsCopied(false)
      }, copiedDuration)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }, [textToCopy, copiedDuration, onCopy])

  return (
    <Button
      variant={variant}
      onClick={handleCopy}
      className={cn(
        'whitespace-nowrap transition-all duration-200',
        isCopied &&
          'border-signal-success bg-signal-success/10 text-signal-success hover:bg-signal-success/15 hover:border-signal-success',
        className,
      )}
      {...props}
    >
      {isCopied ? (
        <>
          <Check className="size-4 shrink-0" />
          {copiedText}
        </>
      ) : (
        <>
          <Copy className="size-4 shrink-0" />
          {children}
        </>
      )}
    </Button>
  )
}
